import { RecordCollection } from '../../app/record-collection.js';
import { RegexGuide } from '../../app/regex-guide.js';
import {
  assertAmountRange,
  assertTypedPattern,
  buildSimplePattern,
  type ReconciliationRuleOperator,
} from '@hue/contracts';
import {
  Button,
  Card,
  CardHeader,
  Combobox,
  CurrencyInput,
  FormErrorSummary,
  Input,
  Modal,
  SegmentedControl,
  Select,
  StatusPill,
  formatCivilDate,
  formatCurrency,
  useSnackbar,
  IconButton,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Wand2, PencilLine, RefreshCw, Trash2 } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import {
  EmptyCard,
  ErrorCard,
  ListFilterBar,
  LoadingCard,
  RequestAlert,
} from '../../app/data-view.js';
import type { ClassificationRulesView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';
import { getApiError, getApiErrorField } from '../../lib/api.js';
import {
  accountingApi,
  classificationRulesApi,
  type AccountingAccount,
  type ClassificationRule,
  type ClassificationRuleInput,
  type RuleAction,
  type RulePreview,
} from './api.js';
import { postableAccounts } from './classification-texts.js';
import { ruleTrials } from './rule-trials.js';

const KEY = ['company', 'accounting', 'rules'] as const;
const operators = [
  { value: 'CONTAINS', label: 'contém' },
  { value: 'STARTS_WITH', label: 'começa com' },
  { value: 'ENDS_WITH', label: 'termina com' },
  { value: 'EQUALS', label: 'é exatamente' },
  { value: 'ANY_OF', label: 'contém uma destas expressões' },
];

export function ClassificationRules({
  view,
  onView,
}: {
  view: ClassificationRulesView;
  onView: ViewSetter<ClassificationRulesView>;
}) {
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const rules = useQuery({ queryKey: KEY, queryFn: classificationRulesApi.list });
  const accounts = useQuery({
    queryKey: ['company', 'accounting', 'accounts'],
    queryFn: accountingApi.accounts,
  });
  const { filter, search } = view;
  const [editing, setEditing] = useState<ClassificationRule | null>();
  const [removing, setRemoving] = useState<ClassificationRule>();
  const [preview, setPreview] = useState<{
    rule: ClassificationRule;
    action: RuleAction;
    data: RulePreview;
  }>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const items = rules.data ?? [];
  async function run(action: () => Promise<unknown>, message: string) {
    setBusy(true);
    setError(undefined);
    try {
      const result = await action();
      await client.invalidateQueries({ queryKey: ['company', 'accounting'] });
      setEditing(undefined);
      setRemoving(undefined);
      setPreview(undefined);
      snackbar.success(typeof result === 'string' ? result : message);
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setBusy(false);
    }
  }
  async function move(index: number, step: number) {
    const order = items.map((rule) => rule.id);
    [order[index], order[index + step]] = [order[index + step]!, order[index]!];
    await run(() => classificationRulesApi.reorder(order), 'Prioridade atualizada.');
  }
  async function showPreview(rule: ClassificationRule, action: RuleAction) {
    setBusy(true);
    setError(undefined);
    try {
      setPreview({ rule, action, data: await classificationRulesApi.preview(rule.id, action) });
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setBusy(false);
    }
  }
  const plain = (text: string) =>
    text
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();
  const shown = items.filter(
    (rule) =>
      (filter === 'ALL' || (filter === 'SUSPENDED') === rule.suspended) &&
      plain(`${rule.name} ${rule.account.code} ${rule.account.name}`).includes(plain(search)),
  );
  function ruleActions(rule: ClassificationRule) {
    const index = items.findIndex((item) => item.id === rule.id);
    return (
      <div className="flex flex-wrap justify-end gap-1">
        <IconButton
          label={`Subir ${rule.name}`}
          icon={<ArrowUp />}
          disabled={busy || index === 0}
          onClick={() => void move(index, -1)}
        />
        <IconButton
          label={`Descer ${rule.name}`}
          icon={<ArrowDown />}
          disabled={busy || index === items.length - 1}
          onClick={() => void move(index, 1)}
        />
        <IconButton
          label={`Editar ${rule.name}`}
          icon={<PencilLine />}
          disabled={busy}
          onClick={() => {
            setError(undefined);
            setEditing(rule);
          }}
        />
        <IconButton
          label={`Aplicar aos sem conta (${rule.applyCount})`}
          icon={<Wand2 />}
          disabled={busy || rule.suspended || rule.applyCount === 0}
          onClick={() => void showPreview(rule, 'apply')}
        />
        <IconButton
          label={`Reclassificar (${rule.reclassifyCount})`}
          icon={<RefreshCw />}
          disabled={busy || rule.suspended || rule.reclassifyCount === 0}
          onClick={() => void showPreview(rule, 'reclassify')}
        />
        <IconButton
          label={`Remover ${rule.name}`}
          icon={<Trash2 />}
          tone="danger"
          disabled={busy}
          onClick={() => {
            setError(undefined);
            setRemoving(rule);
          }}
        />
      </div>
    );
  }
  return (
    <section className="min-w-0">
      <PageHeader
        trail="Contabilidade gerencial / Regras de classificação"
        title="Regras de classificação"
        description="A primeira regra compatível tem prioridade. Aplicar exige confirmação; escolhas manuais e contas dos compromissos são preservadas."
        actions={
          <>
            <CreateButton
              onClick={() => {
                setError(undefined);
                setEditing(null);
              }}
            >
              Nova regra
            </CreateButton>
          </>
        }
      />
      <ListFilterBar
        searchLabel="Buscar regras"
        searchPlaceholder="Buscar regras"
        search={search}
        onSearchChange={(value) => onView({ search: value }, { replace: true })}
        count={`${shown.length} ${shown.length === 1 ? 'regra' : 'regras'}`}
      >
        <SegmentedControl
          label="Situação das regras"
          compact
          value={filter}
          onChange={(filter) => onView({ filter })}
          options={[
            { value: 'ALL', label: 'Todas' },
            { value: 'VALID', label: 'Valendo' },
            { value: 'SUSPENDED', label: 'Suspensas' },
          ]}
        />
      </ListFilterBar>
      <div className="grid gap-4">
        {rules.isLoading || accounts.isLoading ? <LoadingCard label="Carregando regras" /> : null}
        {rules.error || accounts.error ? (
          <ErrorCard message={getApiError(rules.error ?? accounts.error)} />
        ) : null}
        {error && !preview && !removing ? <RequestAlert message={error} /> : null}
        {!rules.isLoading && !rules.error && shown.length === 0 ? (
          <EmptyCard
            icon={Wand2}
            title="Nenhuma regra encontrada"
            description="Ajuste a busca e o filtro ou cadastre a primeira regra."
          />
        ) : null}
        <RecordCollection
          label="Regras de classificação"
          records={shown}
          recordKey={(rule) => rule.id}
          columns={[
            {
              key: 'priority',
              label: 'Prioridade',
              value: (rule) => items.findIndex((item) => item.id === rule.id),
              render: (rule) => `${String(items.findIndex((item) => item.id === rule.id) + 1)}º`,
            },
            {
              key: 'name',
              label: 'Regra',
              value: (rule) => rule.name,
              render: (rule) => <strong>{rule.name}</strong>,
            },
            {
              key: 'condition',
              label: 'Condição',
              render: (rule) => (
                <span className="text-xs">
                  {rule.mode === 'REGEX'
                    ? `Expressão regular: ${rule.pattern}`
                    : `${operators.find((operator) => operator.value === rule.operator)?.label ?? 'contém'} ${rule.terms.join(', ')}`}
                </span>
              ),
            },
            {
              key: 'limits',
              label: 'Aplicação',
              render: (rule) => (
                <div className="text-xs text-content-muted">
                  <p>
                    {rule.direction === null
                      ? 'Entradas e saídas'
                      : rule.direction === 'INFLOW'
                        ? 'Só entradas'
                        : 'Só saídas'}
                    {rule.minAmountCents === null
                      ? ''
                      : ` · a partir de ${formatCurrency(rule.minAmountCents, 'BRL')}`}
                    {rule.maxAmountCents === null
                      ? ''
                      : ` · até ${formatCurrency(rule.maxAmountCents, 'BRL')}`}
                  </p>
                  {rule.suspended ? (
                    <p>
                      A conta do plano não recebe novos lançamentos. Escolha outra conta do plano
                      para a regra voltar a valer.
                    </p>
                  ) : null}
                </div>
              ),
            },
            {
              key: 'account',
              label: 'Conta do plano',
              value: (rule) => rule.account.code,
              render: (rule) => (
                <span>
                  {rule.account.code} {rule.account.name}
                </span>
              ),
            },
            {
              key: 'status',
              label: 'Situação',
              value: (rule) => Number(rule.suspended),
              render: (rule) => (
                <div>
                  <StatusPill tone={rule.suspended ? 'warning' : 'positive'}>
                    {rule.suspended ? 'Suspensa' : 'Valendo'}
                  </StatusPill>
                  <p className="text-xs text-content-muted">
                    Sem classificação: {rule.applyCount} · Por esta regra: {rule.reclassifyCount}
                  </p>
                </div>
              ),
            },
          ]}
          actions={ruleActions}
          card={(rule) => {
            const index = items.findIndex((item) => item.id === rule.id);
            return (
              <Card>
                <CardHeader
                  icon={<Wand2 className="size-4" />}
                  title={`${index + 1}º · ${rule.name}`}
                  action={
                    <StatusPill tone={rule.suspended ? 'warning' : 'positive'}>
                      {rule.suspended ? 'Suspensa' : 'Valendo'}
                    </StatusPill>
                  }
                />
                <p className="break-words text-sm">
                  {rule.mode === 'REGEX'
                    ? `Expressão regular: ${rule.pattern}`
                    : `${operators.find((operator) => operator.value === rule.operator)?.label ?? 'contém'} ${rule.terms.join(', ')}`}{' '}
                  → {rule.account.code} {rule.account.name}
                </p>
                <p className="mt-1 text-sm text-content-muted">
                  {rule.direction === null
                    ? 'Entradas e saídas'
                    : rule.direction === 'INFLOW'
                      ? 'Só entradas'
                      : 'Só saídas'}
                  {rule.minAmountCents === null
                    ? ''
                    : ` · a partir de ${formatCurrency(rule.minAmountCents, 'BRL')}`}
                  {rule.maxAmountCents === null
                    ? ''
                    : ` · até ${formatCurrency(rule.maxAmountCents, 'BRL')}`}
                </p>
                {rule.suspended ? (
                  <p className="mt-1 text-sm text-content-muted">
                    A conta do plano não recebe novos lançamentos. Escolha outra conta do plano para
                    a regra voltar a valer.
                  </p>
                ) : null}
                <p className="mt-2 text-xs text-content-muted">
                  Sem conta: {rule.applyCount} · Por esta regra: {rule.reclassifyCount}
                </p>
                {ruleActions(rule)}
              </Card>
            );
          }}
        />
        <p className="text-sm text-content-muted">
          Salvar, editar, reordenar ou remover não muda classificações existentes. As regras podem
          ser aplicadas automaticamente na importação do extrato e ao desfazer uma conciliação. Para
          o histórico, use Aplicar aos sem conta ou Reclassificar e confira a prévia.
        </p>
        {editing !== undefined ? (
          <RuleDialog
            rule={editing}
            rules={items}
            accounts={accounts.data ?? []}
            onClose={() => setEditing(undefined)}
            onSaved={() => run(async () => {}, 'Regra salva.')}
          />
        ) : null}
        {removing ? (
          <Modal
            open
            onOpenChange={() => setRemoving(undefined)}
            title={`Remover ${removing.name}`}
            description="As classificações existentes continuam com o nome da regra e a indicação Regra removida."
            footer={
              <>
                <Button variant="outline" disabled={busy} onClick={() => setRemoving(undefined)}>
                  Voltar
                </Button>
                <Button
                  loading={busy}
                  onClick={() =>
                    void run(() => classificationRulesApi.remove(removing.id), 'Regra removida.')
                  }
                >
                  Remover
                </Button>
              </>
            }
          >
            {error ? <RequestAlert message={error} /> : null}
          </Modal>
        ) : null}
        {preview ? (
          <Modal
            open
            onOpenChange={() => setPreview(undefined)}
            title={`${preview.action === 'apply' ? 'Aplicar aos sem conta' : 'Reclassificar'} · ${preview.rule.name}`}
            description={`${preview.data.count} partes · ${formatCurrency(preview.data.totalCents, 'BRL')} → ${preview.rule.account.code} ${preview.rule.account.name}.`}
            footer={
              <>
                <Button variant="outline" disabled={busy} onClick={() => setPreview(undefined)}>
                  Voltar
                </Button>
                <Button
                  loading={busy}
                  disabled={preview.data.count === 0}
                  onClick={() =>
                    void run(async () => {
                      const { count, skippedClosed } = await classificationRulesApi.apply(
                        preview.rule.id,
                        preview.action,
                      );
                      return skippedClosed > 0
                        ? `${count} partes classificadas. ${skippedClosed} ficaram como estavam porque são de mês fechado.`
                        : `${count} partes classificadas.`;
                    }, 'Aplicação concluída.')
                  }
                >
                  Confirmar
                </Button>
              </>
            }
          >
            <p className="text-sm">
              A confirmação verifica novamente as partes elegíveis. O total pode mudar se outro
              lançamento for classificado antes.
            </p>
            {preview.data.skippedClosed > 0 ? (
              <p className="mt-2 text-sm text-content-muted">
                {preview.data.skippedClosed === 1
                  ? 'Uma parte que a regra pegaria é de mês fechado e fica como está.'
                  : `${preview.data.skippedClosed} partes que a regra pegaria são de mês fechado e ficam como estão.`}{' '}
                Para mudá-las, reabra o mês no Fechamento ou classifique uma a uma lançando ajuste.
              </p>
            ) : null}
            <ul className="mt-3 grid gap-2">
              {preview.data.sample.map((sample, index) => (
                <li key={index} className="text-sm">
                  {sample.description} · {formatCivilDate(sample.date)} ·{' '}
                  {formatCurrency(sample.amountCents, 'BRL')}
                </li>
              ))}
            </ul>
            {preview.data.count > preview.data.sample.length ? (
              <p className="mt-2 text-sm">
                Mostrando uma amostra de {preview.data.sample.length} partes.
              </p>
            ) : null}
            {error ? <RequestAlert message={error} /> : null}
          </Modal>
        ) : null}
      </div>
    </section>
  );
}

function RuleDialog({
  rule,
  rules,
  accounts,
  onClose,
  onSaved,
}: {
  rule: ClassificationRule | null;
  rules: ClassificationRule[];
  accounts: AccountingAccount[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const formId = useId();
  const [name, setName] = useState(rule?.name ?? '');
  const [mode, setMode] = useState<'SIMPLE' | 'REGEX'>(rule?.mode ?? 'SIMPLE');
  const [operator, setOperator] = useState<ReconciliationRuleOperator>(
    rule?.operator ?? 'CONTAINS',
  );
  const [terms, setTerms] = useState(rule?.terms.join(', ') ?? '');
  const [pattern, setPattern] = useState(rule?.mode === 'REGEX' ? rule.pattern : '');
  const [direction, setDirection] = useState<'' | 'INFLOW' | 'OUTFLOW'>(rule?.direction ?? '');
  const [minimum, setMinimum] = useState<number | null>(rule?.minAmountCents ?? null);
  const [maximum, setMaximum] = useState<number | null>(rule?.maxAmountCents ?? null);
  const [accountId, setAccountId] = useState(rule?.accountId ?? '');
  const [error, setError] = useState<string>();
  const [field, setField] = useState<string>();
  const [busy, setBusy] = useState(false);
  const candidates = useQuery({
    queryKey: [...KEY, 'candidates'],
    queryFn: classificationRulesApi.candidates,
  });
  const options = postableAccounts(accounts).map((account) => ({
    value: account.id,
    label: `${account.code} ${account.name}`,
  }));
  const input: ClassificationRuleInput = {
    name,
    mode,
    operator,
    terms:
      mode === 'SIMPLE' && operator === 'ANY_OF'
        ? terms
            .split(',')
            .map((term) => term.trim())
            .filter(Boolean)
        : [terms.trim()],
    pattern,
    direction: direction === '' ? null : direction,
    minAmountCents: minimum,
    maxAmountCents: maximum,
    accountId,
  };
  let compiledPattern = '';
  let flaw: string | undefined;
  try {
    assertAmountRange(minimum, maximum);
    compiledPattern =
      mode === 'REGEX'
        ? assertTypedPattern(pattern)
        : buildSimplePattern(operator, input.terms ?? []);
  } catch (cause) {
    flaw = cause instanceof Error ? cause.message : 'Condição incompleta.';
  }
  const trials = flaw
    ? []
    : ruleTrials(
        {
          ...input,
          id: rule?.id ?? 'draft',
          pattern: compiledPattern,
          suspended: !options.some((option) => option.value === accountId),
        },
        rules,
        candidates.data ?? [],
      );
  async function save(event: FormEvent) {
    event.preventDefault();
    setField(undefined);
    setError(undefined);
    if (name.trim().length < 2 || name.trim().length > 120) {
      setField('name');
      setError('Dê à regra um nome de 2 a 120 caracteres.');
      return;
    }
    if (!options.some((option) => option.value === accountId)) {
      setField('accountId');
      setError('Escolha uma conta ativa que receba lançamentos.');
      return;
    }
    if (flaw) {
      setField('condition');
      setError(flaw);
      return;
    }
    setBusy(true);
    try {
      if (rule) await classificationRulesApi.update(rule.id, input);
      else await classificationRulesApi.create(input);
      await onSaved();
    } catch (cause) {
      setField(getApiErrorField(cause));
      setError(getApiError(cause));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={onClose}
      title={rule ? `Editar ${rule.name}` : 'Nova regra de classificação'}
      description="Acentos e maiúsculas não mudam o resultado. Salvar não aplica a regra ao passado."
      footer={
        <>
          <FormErrorSummary form={formId} />
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" form={formId} loading={busy}>
            Salvar regra
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={(event) => void save(event)} className="grid gap-4">
        <Input
          label="Nome da regra"
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={field === 'name' ? error : undefined}
        />
        <Combobox
          label="Conta do plano"
          placeholder="Digite o nome ou código"
          selectedLabel={
            options.find((option) => option.value === accountId)?.label ??
            (accountId === rule?.accountId ? rule.account.name : '')
          }
          options={options}
          onSelect={(choice) => {
            if (choice.kind === 'option') setAccountId(choice.value);
          }}
          onClear={() => setAccountId('')}
          error={field === 'accountId' ? error : undefined}
        />
        <SegmentedControl
          label="Modo da condição"
          value={mode}
          onChange={(value) => setMode(value)}
          options={[
            { value: 'SIMPLE', label: 'Texto' },
            { value: 'REGEX', label: 'Expressão regular' },
          ]}
        />
        {mode === 'SIMPLE' ? (
          <>
            <Select
              label="Quando a descrição"
              value={operator}
              onValueChange={(value) => setOperator(value as ReconciliationRuleOperator)}
              options={operators}
            />
            <Input
              label={operator === 'ANY_OF' ? 'Textos separados por vírgula' : 'Texto procurado'}
              value={terms}
              onChange={(event) => setTerms(event.target.value)}
              error={field === 'condition' ? error : undefined}
            />
          </>
        ) : (
          <Input
            label="Expressão regular"
            value={pattern}
            onChange={(event) => setPattern(event.target.value)}
            error={field === 'condition' ? error : undefined}
          />
        )}
        <RegexGuide />
        <Select
          label="Direção"
          value={direction}
          onValueChange={(value) => setDirection(value as typeof direction)}
          options={[
            { value: '', label: 'Entradas e saídas' },
            { value: 'INFLOW', label: 'Só entradas' },
            { value: 'OUTFLOW', label: 'Só saídas' },
          ]}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <CurrencyInput
            label="Valor mínimo (opcional)"
            currency="BRL"
            value={minimum}
            onValueChange={setMinimum}
          />
          <CurrencyInput
            label="Valor máximo (opcional)"
            currency="BRL"
            value={maximum}
            onValueChange={setMaximum}
          />
        </div>
        <Card>
          <CardHeader title="Testador da regra" icon={<Wand2 className="size-4" />} />
          <p className="text-sm text-content-muted">
            Teste das primeiras 200 partes disponíveis nas unidades do seu alcance, em todos os
            períodos. Nada é gravado. Partes já conciliadas usam a conta do compromisso e não entram
            no teste.
          </p>
          {flaw ? <p className="mt-2 text-sm text-content-muted">{flaw}</p> : null}
          {candidates.isLoading ? <p>Carregando partes…</p> : null}
          {candidates.error ? <RequestAlert message={getApiError(candidates.error)} /> : null}
          {!flaw && !candidates.isLoading && !candidates.error && trials.length === 0 ? (
            <p className="mt-2 text-sm">Nenhuma parte desta amostra combinou.</p>
          ) : null}
          <ul className="mt-2 grid gap-2">
            {trials.slice(0, 20).map((trial) => (
              <li key={trial.key} className="break-words text-sm">
                {trial.description} · {formatCurrency(trial.amountCents, 'BRL')} · {trial.effect}
              </li>
            ))}
          </ul>
          {trials.length > 20 ? (
            <p>Mostrando 20 de {trials.length} resultados da amostra.</p>
          ) : null}
        </Card>
        {error ? <RequestAlert message={error} /> : null}
      </form>
    </Modal>
  );
}
